import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";
import { format, isValid, parse } from "date-fns";

import { getLabelWithTranslationFallback } from "helpers/getLabel";
import Avatar from "components/v2/Avatar/Avatar";
import Card from "components/v2/Card/Card";
import FilePreview from "components/v2/FilePreview/FilePreview";
import { useDraftProviderOptions } from "@RequestFormV2/hooks/useDraftProviderOptions";
import type { BucketFileEntry, OtherFileState } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";
import type { InvitedProvidersFormValue, ProviderOption } from "@RequestFormV2/sections/Provider/types";
import { isProviderEntrySelected } from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";

function useSelectedProviders(): ProviderOption[] {
  const invitedProviders = useWatch({ name: "invited_providers" }) as InvitedProvidersFormValue | undefined;
  const { providerOptions } = useDraftProviderOptions({ enabled: true });
  const selectedEntries = invitedProviders?.providers ?? [];

  return useMemo(
    () => providerOptions.filter((provider) => isProviderEntrySelected(provider, selectedEntries)),
    [providerOptions, selectedEntries],
  );
}

function getParcelAccountName(
  provider: ProviderOption,
  parcelAccounts: InvitedProvidersFormValue["parcel_accounts"],
): string | undefined {
  if (!provider.parcel_accounts?.length || !parcelAccounts?.length) return undefined;
  const selected = parcelAccounts.find((pa) => isProviderEntrySelected(provider, [pa.provider_id]));
  if (!selected) return undefined;
  const account = provider.parcel_accounts.find((a) => a.id === selected.parcel_account_id);
  return account?.account_name;
}

function formatDeadline(date?: string, time?: string): string | undefined {
  if (!date) return undefined;
  const combined = time ? `${date} ${time}` : date;
  const parseFormat = time?.length === 8 ? "yyyy-MM-dd HH:mm:ss" : time ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd";
  const parsed = parse(combined, parseFormat, new Date());
  if (!isValid(parsed)) return combined;
  return time ? format(parsed, "dd MMM yyyy HH:mm") : format(parsed, "dd MMM yyyy");
}

export default function ReviewProviders() {
  const intl = useIntl();
  const invitedProviders = useWatch({ name: "invited_providers" }) as InvitedProvidersFormValue | undefined;
  const bucketAttachments = useWatch({ name: "bucket_attachments" }) as BucketFileEntry[] | undefined;
  const otherAttachments = useWatch({ name: "other_attachments" }) as OtherFileState[] | undefined;
  const quotingDeadlineDate = useWatch({ name: "shipment.quoting_deadline_date" }) as string | undefined;
  const quotingDeadlineTime = useWatch({ name: "shipment.quoting_deadline_time" }) as string | undefined;
  const confirmationDate = useWatch({ name: "shipment.confirmation_date" }) as string | undefined;
  const confirmationTime = useWatch({ name: "shipment.confirmation_time" }) as string | undefined;

  const selectedProviders = useSelectedProviders();

  const quotingDeadline = formatDeadline(quotingDeadlineDate, quotingDeadlineTime);
  const confirmationDeadline = formatDeadline(confirmationDate, confirmationTime);

  const bucketFiles = useMemo(() => (bucketAttachments ?? []).filter((f) => f?.filename), [bucketAttachments]);
  const otherFiles = useMemo(
    () => (otherAttachments ?? []).filter((f): f is OtherFileState => !!f && !!f.name && !f.isDeleting),
    [otherAttachments],
  );

  const bucketGroups = useMemo(
    () =>
      bucketFiles.reduce<Record<string, BucketFileEntry[]>>((groups, file) => {
        const bucket = file.bucket_name || "Other";
        const existing = groups[bucket] ?? [];
        return { ...groups, [bucket]: [...existing, file] };
      }, {}),
    [bucketFiles],
  );

  const logoUrl = (provider: ProviderOption) =>
    typeof provider.company_logo === "string" ? provider.company_logo : undefined;

  return (
    <Card borderRadius="xl" shadow="xs">
      <Card.Header
        title={intl.formatMessage({
          id: "request-form.provider",
          defaultMessage: "Provider",
        })}
        showBorder
      />
      <Card.Body>
        <div className="rfv2-review__section-content">
          <div className="rfv2-review__deadlines">
            <div className="rfv2-review__field">
              <span className="rfv2-review__field-label">
                {getLabelWithTranslationFallback("Quoting deadline", {
                  translation: intl.formatMessage({
                    id: "request-form.provider.deadlines.quotingDeadline",
                    defaultMessage: "Quoting deadline",
                  }),
                  returnAsString: true,
                  withoutInfo: true,
                })}
              </span>
              <span className="rfv2-review__field-value">
                {quotingDeadline || <span className="rfv2-review__empty">-</span>}
              </span>
            </div>
            <div className="rfv2-review__field">
              <span className="rfv2-review__field-label">
                {getLabelWithTranslationFallback("Confirmation time", {
                  translation: intl.formatMessage({
                    id: "request-form.review.providers.confirmationDeadline",
                    defaultMessage: "Confirmation deadline",
                  }),
                  returnAsString: true,
                  withoutInfo: true,
                })}
              </span>
              <span className="rfv2-review__field-value">
                {confirmationDeadline || <span className="rfv2-review__empty">-</span>}
              </span>
            </div>
          </div>

          <div className="rfv2-review__service_field">
            <span className="rfv2-review__field-label">
              {intl.formatMessage({
                id: "request-form.review.providers.count",
                defaultMessage: "Invited providers",
              })}
            </span>
            {selectedProviders.length > 0 ? (
              <div className="rfv2-review__provider-grid">
                {selectedProviders.map((provider) => {
                  const teamName = getParcelAccountName(provider, invitedProviders?.parcel_accounts);
                  return (
                    <div key={provider.id} className="rfv2-review__provider-cell">
                      <Avatar size="sm" image={logoUrl(provider)} name={provider.company_name} />
                      <div className="rfv2-review__provider-info">
                        <span className="rfv2-review__provider-name">{provider.company_name}</span>
                        {teamName && <span className="rfv2-review__provider-team">{teamName}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="rfv2-review__empty">
                {intl.formatMessage({
                  id: "request-form.review.providers.none",
                  defaultMessage: "No providers invited",
                })}
              </span>
            )}
          </div>

          {(bucketFiles.length > 0 || otherFiles.length > 0) && (
            <div className="rfv2-review__service_field">
              <span className="rfv2-review__field-label">
                {intl.formatMessage({
                  id: "request-form.review.providers.freightDocuments",
                  defaultMessage: "Freight documents",
                })}
              </span>

              {Object.keys(bucketGroups).length > 0 && (
                <div className="rfv2-review__docs-grid">
                  {Object.entries(bucketGroups).map(([bucketName, files]) =>
                    files.map((file) => (
                      <div key={file.id} className="rfv2-review__doc-cell">
                        <span className="rfv2-review__doc-bucket-name">{bucketName}</span>
                        <FilePreview fileName={file.filename} url={file.url} />
                      </div>
                    )),
                  )}
                </div>
              )}

              {otherFiles.length > 0 && (
                <div className="rfv2-review__docs-others">
                  <span className="rfv2-review__field-label">
                    {intl.formatMessage({
                      id: "request-form.review.providers.otherDocuments",
                      defaultMessage: "Others",
                    })}
                  </span>
                  <div className="rfv2-review__docs-grid">
                    {otherFiles.map((file) => (
                      <div key={file.id} className="rfv2-review__doc-cell">
                        <FilePreview fileName={file.name} url={file.url} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
